import { useState, useEffect, useCallback } from 'react';

export function getInitialTheme() {
  if (typeof window === 'undefined') return 'light';
  const saved = localStorage.getItem('theme');
  if (saved === 'light' || saved === 'dark') return saved;
  return 'light';
}

export function setThemeMode(theme) {
  if (typeof window === 'undefined') return;
  const targetTheme = theme === 'light' ? 'light' : 'dark';
  localStorage.setItem('theme', targetTheme);
  document.documentElement.classList.toggle('light', targetTheme === 'light');
  window.dispatchEvent(new CustomEvent('themechange', { detail: targetTheme }));
}

export function useTheme() {
  const [theme, setThemeState] = useState(getInitialTheme);

  useEffect(() => {
    const handleThemeChange = (e) => {
      if (e.detail === 'light' || e.detail === 'dark') {
        setThemeState(e.detail);
      } else {
        setThemeState(getInitialTheme());
      }
    };

    const handleStorage = (e) => {
      if (e.key === 'theme') {
        const next = e.newValue === 'light' ? 'light' : 'dark';
        setThemeState(next);
        document.documentElement.classList.toggle('light', next === 'light');
      }
    };

    window.addEventListener('themechange', handleThemeChange);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('themechange', handleThemeChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const setTheme = useCallback((nextTheme) => {
    setThemeMode(nextTheme);
    setThemeState(nextTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
  }, [theme, setTheme]);

  return { theme, setTheme, toggleTheme, isDark: theme === 'dark', isLight: theme === 'light' };
}
